import React, { useState } from "react";
import {
	Box,
	CircularProgress,
	IconButton,
	ListItem,
	ListItemButton,
	ListItemText,
	Menu,
	MenuItem,
	MenuList,
	Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { DeleteIcon, EditFilterIcon, EditIcon, PinIcon, PinOutlineIcon } from "../../../../../../assets/icons";
import { isFilterNameAvailable } from "../../../../../../common/FiltersFormEngine/querySelectors";
import { useFiltersRegistry } from "../../../../../../context/FiltersProvider";
import { useFilterFavorite } from "../../../../../../context/FiltersProvider/hooks/useFilterFavorite";
import { FilterDefinition } from "../../../../../../context/FiltersProvider/types";
import { UserCustomFilterPayload } from "../../../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../../../datasources/networking/apiRoutes";
import { useWriteResource } from "../../../../../../hooks/useResource";
import { DashboardAdvancedModeModal } from "../../../../Dashboard/components/DashboardAdvancedModeDialog";
import { FilterNamePopover } from "../../../../Dashboard/components/FilterNamePopover";

type Props = {
	filter: FilterDefinition;
};

export const FiltersListItem: React.FC<Props> = ({ filter }) => {
	const { t } = useTranslation("common");
	const {
		addDashboardFilter,
		registry,
		dashboardFilters,
		renameCustomFilter,
		removeCustomFilter,
		applySavedFilter,
		applyAdvancedConfiguration,
	} = useFiltersRegistry();
	const { toggleFavorite, isLoading } = useFilterFavorite(filter.id, filter.favorite);

	const [openDashboardAdvancedModal, setOpenDashboardAdvancedModal] = useState(false);

	const {
		put: editCustomFilter,
		del: deleteCustomFilter,
		isLoading: isEditing,
	} = useWriteResource<UserCustomFilterPayload, undefined>(
		{ route: ApiRoutes.CustomMenuFilters, path: { id: filter.id } }, 
		{ subjectKey: "customfilter" }
	);

	// MENU
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	// RENAME POPOVER
	const [renameAnchorEl, setRenameAnchorEl] = useState<HTMLElement | null>(null);

	const openMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		setAnchorEl(e.currentTarget);
	};

	const closeMenu = () => {
		setAnchorEl(null);
	};

	// ACTIONS
	const handleRenameOpen = () => {
		setRenameAnchorEl(anchorEl);
		closeMenu();
	};

	const handleRenameClose = () => {
		setRenameAnchorEl(null);
	};

	const handleDelete = async () => {
		closeMenu();
		await deleteCustomFilter();
		removeCustomFilter(filter.id);
	};

	const handleSaveRename = async (name: string) => {
		const payload: UserCustomFilterPayload = {
			name,
			isComplex: filter.filterType === "configuration",
			filter: filter.filter,
		};

		await editCustomFilter(payload);

		renameCustomFilter(filter.id, name);

		handleRenameClose();
	};

	// FAVORITE
	const isFavorite = !!filter.favorite;

	const handleToggleFavorite = (e: React.MouseEvent) => {
		e.stopPropagation();
		toggleFavorite();
	};

	// SELECT
	const handleSelect = () => {
		// AVAILABLE FILTER
		if (filter.source === "system") {
			addDashboardFilter(filter.id);
			return;
		}

		// SAVED SIMPLE FILTER
		if (filter.filterType === "simple") {
			applySavedFilter(filter.id);
			return;
		}

		// ADVANCED CONFIGURATION
		if (filter.filterType === "configuration") {
			const hasSimpleFilters = dashboardFilters.some((f) => f.mode === "simple");
			if (hasSimpleFilters) {
				setOpenDashboardAdvancedModal(true);
				return;
			}

			applyAdvancedConfiguration(filter.filter);
		}
	};

	const handleConfirmAdvancedMode = () => {
		applyAdvancedConfiguration(filter.filter);
		setOpenDashboardAdvancedModal(false);
	};

	return (
		<>
			<ListItem sx={{ p: 0 }} disableGutters>
				<ListItemButton onClick={handleSelect}>
					<ListItemText primary={filter.name} />
				</ListItemButton>

				<Box
					display="flex"
					alignItems="center"
					sx={{ position: "absolute", right: "16px", top: "50%", transform: "translateY(-50%)" }}
				>
					{filter.ui?.canPin && (
						<Tooltip title={isFavorite ? t("remove from favourite") : t("mark as favourite")}>
							<IconButton onClick={handleToggleFavorite} size="small" sx={{ width: 34, height: 34 }}>
								{isLoading ? (
									<CircularProgress size={16} />
								) : isFavorite ? (
									<PinIcon fontSize="small" />
								) : (
									<PinOutlineIcon fontSize="small" />
								)}
							</IconButton>
						</Tooltip>
					)}

					{filter.ui?.hasMenu && (
						<IconButton onClick={openMenu} size="small" sx={{ width: 34, height: 34 }}>
							{isEditing ? <CircularProgress size={16} /> : <EditFilterIcon />}
						</IconButton>
					)}
				</Box>
			</ListItem>

			<Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
				<MenuList sx={{ width: "260px" }}>
					<MenuItem
						onClick={handleRenameOpen}
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							height: 48,
						}}
					>
						{t("rename")}
						<EditIcon fontSize="small" />
					</MenuItem>

					<MenuItem
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							height: 48,
						}}
						onClick={handleDelete}
					>
						{t("delete")}
						<DeleteIcon fontSize="small" />
					</MenuItem>
				</MenuList>
			</Menu>

			<DashboardAdvancedModeModal
				title={t("dashboard/title add filter", { filterName: filter.name })}
				paragraphs={
					t("dashboard/list add filter", { filterName: filter.name, returnObjects: true }) as string[]
				}
				open={openDashboardAdvancedModal}
				onClose={() => setOpenDashboardAdvancedModal(false)}
				onConfirm={handleConfirmAdvancedMode}
			/>

			{/* Rename popover */}
			<FilterNamePopover
				anchorEl={renameAnchorEl}
				open={Boolean(renameAnchorEl)}
				title={t("set filter name")}
				initialValue={filter.name}
				onClose={handleRenameClose}
				onSave={handleSaveRename}
				validateName={(value) =>
					isFilterNameAvailable({
						registry,
						name: value,
					})
				}
			/>
		</>
	);
};
